import { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { Layout } from "@/components/layout/Layout";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { ArrowLeft, Loader2, MessageSquare, Sparkles, Clock, MapPin, Ruler, Palette, Home, Send, Eye, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import type { Json } from "@/integrations/supabase/types";
import { ConciergeThread } from "@/components/concierge/ConciergeThread";

interface ConciergeRequest {
  id: string;
  initial_description: string;
  space_type: string | null;
  area_size: string | null;
  project_location: string | null;
  material_preference: string | null;
  style_direction: string | null;
  summary: Json | null;
  recommendations: Json | null;
  status: string;
  created_at: string;
}

interface ProjectData {
  id: string;
  name: string;
  description: string | null;
  concierge_request_id: string | null;
  created_at: string;
}

export default function Workspace() {
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState<ProjectData | null>(null);
  const [concierge, setConcierge] = useState<ConciergeRequest | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      if (!projectId) return;
      setLoading(true);

      const { data: proj } = await supabase
        .from("projects")
        .select("*")
        .eq("id", projectId)
        .maybeSingle();

      if (proj) {
        setProject(proj as ProjectData);

        if (proj.concierge_request_id) {
          const { data: req } = await supabase
            .from("concierge_requests")
            .select("*")
            .eq("id", proj.concierge_request_id)
            .maybeSingle();
          if (req) setConcierge(req as ConciergeRequest);
        }
      }
      setLoading(false);
    }
    load();
  }, [projectId]);

  const summaryObj = concierge?.summary as Record<string, string> | null;
  const recommendations = concierge?.recommendations as Array<Record<string, string>> | null;

  const timelineSteps = [
    { key: "submitted", label: "Submitted", icon: Send, description: "Request received" },
    { key: "reviewing", label: "Reviewing", icon: Eye, description: "Our team is on it" },
    { key: "responded", label: "Responded", icon: CheckCircle2, description: "Recommendations ready" },
  ];

  function StatusTimeline({ status }: { status: string }) {
    const statusOrder = ["new", "reviewing", "responded"];
    const normalized = status === "new" ? "new" : status;
    const activeIdx = statusOrder.indexOf(normalized);
    const currentStep = activeIdx === -1 ? 0 : activeIdx;

    return (
      <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
        <div className="flex items-center justify-between">
          {timelineSteps.map((step, i) => {
            const isCompleted = i < currentStep || (normalized === "responded" && i <= 2);
            const isCurrent = i === currentStep && normalized !== "responded";
            const StepIcon = step.icon;

            return (
              <div key={step.key} className="flex flex-1 items-center">
                <div className="flex flex-col items-center text-center">
                  <div
                    className={`flex h-10 w-10 items-center justify-center rounded-full border-2 transition-all duration-500 ${
                      isCompleted
                        ? "border-primary bg-primary text-primary-foreground"
                        : isCurrent
                        ? "border-primary bg-primary/10 text-primary animate-[pulse_2s_cubic-bezier(0.4,0,0.6,1)_infinite]"
                        : "border-border bg-muted text-muted-foreground"
                    }`}
                  >
                    <StepIcon className="h-4 w-4" />
                  </div>
                  <p className={`mt-2 text-xs font-medium ${isCompleted || isCurrent ? "text-foreground" : "text-muted-foreground"}`}>
                    {step.label}
                  </p>
                  <p className="text-[10px] text-muted-foreground">{step.description}</p>
                </div>
                {i < timelineSteps.length - 1 && (
                  <div className="mx-2 h-0.5 flex-1 rounded-full bg-border">
                    <div
                      className={`h-full rounded-full bg-primary transition-all duration-700 ${
                        isCompleted ? "w-full" : "w-0"
                      }`}
                    />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    );
  }

  return (
    <Layout hideFooter>
      <div className="flex min-h-[calc(100vh-64px)] flex-col">
        {/* Top bar */}
        <div className="flex items-center gap-4 border-b border-border bg-card px-4 py-3">
          <Button variant="ghost" size="icon" onClick={() => navigate("/projects")}>
            <ArrowLeft className="h-4 w-4" />
          </Button>
          <div className="flex-1">
            <h1 className="font-serif text-lg font-medium text-foreground">
              {project?.name || "Project"}
            </h1>
            {concierge && (
              <p className="text-xs text-muted-foreground">
                Submitted {new Date(concierge.created_at).toLocaleDateString()}
              </p>
            )}
          </div>
          <Badge variant="secondary" className="capitalize">
            {concierge?.status || "draft"}
          </Badge>
        </div>

        {loading ? (
          <div className="flex flex-1 items-center justify-center">
            <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
          </div>
        ) : !concierge ? (
          <div className="flex flex-1 flex-col items-center justify-center p-8 text-center">
            <MessageSquare className="mb-4 h-12 w-12 text-muted-foreground/40" />
            <h3 className="mb-2 font-serif text-lg font-medium text-foreground">
              No request linked
            </h3>
            <p className="text-sm text-muted-foreground">
              This project doesn't have a concierge request associated with it.
            </p>
          </div>
        ) : (
          <div className="flex-1 overflow-y-auto">
            <div className="mx-auto max-w-3xl space-y-8 px-4 py-8">
            {/* Status Timeline */}
              <StatusTimeline status={concierge.status} />

              {/* User's Request */}
              <section>
                <div className="mb-4 flex items-center gap-2">
                  <MessageSquare className="h-5 w-5 text-primary" />
                  <h2 className="font-serif text-xl font-semibold text-foreground">Your Request</h2>
                </div>
                <div className="rounded-xl border border-border bg-card p-6 shadow-sm">
                  <p className="text-foreground leading-relaxed">
                    {concierge.initial_description}
                  </p>

                  {/* Detail chips */}
                  <div className="mt-5 flex flex-wrap gap-3">
                    {concierge.space_type && (
                      <div className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-sm text-muted-foreground">
                        <Home className="h-3.5 w-3.5" />
                        {concierge.space_type}
                      </div>
                    )}
                    {concierge.area_size && (
                      <div className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-sm text-muted-foreground">
                        <Ruler className="h-3.5 w-3.5" />
                        {concierge.area_size}
                      </div>
                    )}
                    {concierge.project_location && (
                      <div className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-sm text-muted-foreground">
                        <MapPin className="h-3.5 w-3.5" />
                        {concierge.project_location}
                      </div>
                    )}
                    {concierge.material_preference && (
                      <div className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-sm text-muted-foreground">
                        <Palette className="h-3.5 w-3.5" />
                        {concierge.material_preference}
                      </div>
                    )}
                    {concierge.style_direction && (
                      <div className="flex items-center gap-1.5 rounded-full bg-muted px-3 py-1.5 text-sm text-muted-foreground">
                        <Sparkles className="h-3.5 w-3.5" />
                        {concierge.style_direction}
                      </div>
                    )}
                  </div>
                </div>
              </section>

              {/* AI Summary / Response */}
              {summaryObj && (
                <section>
                  <div className="mb-4 flex items-center gap-2">
                    <Sparkles className="h-5 w-5 text-accent" />
                    <h2 className="font-serif text-xl font-semibold text-foreground">Our Response</h2>
                  </div>
                  <div className="rounded-xl border border-border bg-card p-6 shadow-sm space-y-4">
                    {Object.entries(summaryObj).map(([key, value]) => (
                      <div key={key}>
                        <h4 className="mb-1 text-sm font-medium capitalize text-muted-foreground">
                          {key.replace(/_/g, " ")}
                        </h4>
                        <p className="text-foreground leading-relaxed">{String(value)}</p>
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Recommendations */}
              {recommendations && recommendations.length > 0 && (
                <section>
                  <div className="mb-4 flex items-center gap-2">
                    <Palette className="h-5 w-5 text-primary" />
                    <h2 className="font-serif text-xl font-semibold text-foreground">
                      Recommendations
                    </h2>
                  </div>
                  <div className="grid gap-4 sm:grid-cols-2">
                    {recommendations.map((rec, i) => (
                      <div
                        key={i}
                        className="rounded-xl border border-border bg-card p-5 shadow-sm transition-all hover:shadow-md"
                      >
                        {Object.entries(rec).map(([key, value]) => (
                          <div key={key} className="mb-2 last:mb-0">
                            <span className="text-xs font-medium uppercase tracking-wider text-muted-foreground">
                              {key.replace(/_/g, " ")}
                            </span>
                            <p className="text-sm text-foreground">{String(value)}</p>
                          </div>
                        ))}
                      </div>
                    ))}
                  </div>
                </section>
              )}

              {/* Pending state */}
              {!summaryObj && !recommendations && concierge.status === "pending" && (
                <section>
                  <div className="flex flex-col items-center rounded-xl border border-border bg-card p-8 text-center shadow-sm">
                    <Clock className="mb-3 h-8 w-8 text-muted-foreground/50" />
                    <h3 className="mb-1 font-serif text-lg font-medium text-foreground">
                      Response pending
                    </h3>
                    <p className="text-sm text-muted-foreground">
                      We're reviewing your request. You'll be notified when it's ready.
                    </p>
                  </div>
                </section>
              )}

              {/* Follow-up Messages */}
              <ConciergeThread conciergeRequestId={concierge.id} />
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
}
